import React, { useCallback, useContext } from "react";
import Panel from "@adsk/alloy-react-panel";
import { useHydratedRef } from "@adsk/acc-issues-common/hooks/dom/useHydratedRef";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";
import { IssueDetailTabPanelSubHeader } from "./IssueDetailTabPanelSubHeader";
import { IssueEditForm } from "./IssueEditForm";
import { CreationDetailsSection } from "./CreationDetailsSection";
import { IssueCommentsPanel } from "./IssueCommentsPanel";
import { DividerLine } from "../../utils/components/DividerLine";
import {
  Issue,
  IssueAttributes,
  MarkupCreationPayload,
} from "@adsk/acc-issues-types";
import { PanelWidthVariation } from "../panelWidthVariations";
import { useTranslations } from "@adsk/acc-issues-translations";
import { PanelContentDividerLine } from "../../utils/components/Panel";
import { DelayRender } from "../../utils/components/DelayRender";
import { IssueCommentsLoader } from "./IssueCommentsLoader";
import { DeletedIssuesSpaceContext } from "@adsk/acc-issues-core/providers/DeletedIssuesSpaceContext";
import { useIssueChangeHandler } from "../../hooks/useIssueChangeHandler";

interface IssueDetailTabPanelBodyProps {
  issue: Issue;
  onDelete?: (issueId: string) => void;
  onChangePublishStatus?: (issueId: string, isNowPublished: boolean) => void;
  onSuccessfulChange?: (
    issueChanges: Partial<IssueAttributes>,
    issueSubResources?: {
      placements?: MarkupCreationPayload | null;
    }
  ) => void;
  widthVariation: PanelWidthVariation;
  withPlacementLink: boolean;
  onRestoreSuccess?: () => void;
  panelOpenedAfterIssueCreation?: boolean;
}

/**
 * This component is the Details tab of the Issue side panel;
 * It is CONTAINED inside IssueDetailPanel.
 */
export const IssueDetailTabPanelBody = ({
  issue,
  onDelete,
  onChangePublishStatus,
  onSuccessfulChange,
  widthVariation,
  withPlacementLink,
  onRestoreSuccess,
  panelOpenedAfterIssueCreation,
}: IssueDetailTabPanelBodyProps) => {
  const ref = useHydratedRef();
  const { showNotification } = useNotifications();
  const { T } = useTranslations();
  const isDeletedIssuesSpaceActive = useContext(DeletedIssuesSpaceContext);

  const onUpdateFailed = useCallback(() => {
    // @TODO: the sdk is no returning any useful error information; that should change
    showNotification({
      status: NOTIFICATION_STATUSES.ERROR,
      message: T.translate("editable.failureMessage"),
    });
  }, [T, showNotification]);

  const { handleChange, errors } = useIssueChangeHandler({
    issue,
    onSuccess: onSuccessfulChange,
    onFailure: onUpdateFailed,
  });

  return (
    <div
      key={issue.id}
      ref={ref}
      style={{
        height: "100%",
      }}
    >
      <IssueDetailTabPanelSubHeader
        issue={issue}
        handleChange={handleChange}
        panelRef={ref}
        widthVariation={widthVariation}
        onDelete={onDelete}
        onChangePublishStatus={onChangePublishStatus}
        onRestoreSuccess={onRestoreSuccess}
      />
      <DividerLine extendPx={-16} />
      <Panel.Body
        style={{
          height: "calc(100% - 76px)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          paddingTop: 0,
        }}
      >
        <IssueEditForm
          issue={issue}
          errors={errors}
          withPlacementLink={withPlacementLink}
          handleChange={handleChange}
          onUpdateFailed={onUpdateFailed}
          viewOnly={isDeletedIssuesSpaceActive}
          panelOpenedAfterIssueCreation={panelOpenedAfterIssueCreation}
        />
        <PanelContentDividerLine />
        <DelayRender delay={600} fallback={<IssueCommentsLoader />}>
          <IssueCommentsPanel
            issue={issue}
            viewOnly={isDeletedIssuesSpaceActive}
          />
        </DelayRender>
        <PanelContentDividerLine />
        <CreationDetailsSection issue={issue} />
      </Panel.Body>
    </div>
  );
};
